import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Database,
  Table as TableIcon,
  Play,
  Eraser,
  Trash2,
  Copy,
  Download,
  ChevronLeft,
  ChevronRight,
  KeyRound,
  Link as LinkIcon,
  Loader2,
} from "lucide-react";

import api from "../../api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Textarea } from "../ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";

const ADMIN_BASE = "/admin/db/api"; // joined to axios baseURL "/api" => "/api/admin/db/api"
const RECENT_KEY = "pydbadmin.recent";
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function formatCell(v) {
  if (v === null || v === undefined) return { text: "NULL", isNull: true, full: null };
  if (typeof v === "object") {
    const j = JSON.stringify(v);
    return { text: j, isNull: false, full: j };
  }
  const s = String(v);
  if (UUID_RE.test(s)) return { text: s.slice(0, 8), isNull: false, full: s, isUuid: true };
  return {
    text: s.length > 200 ? s.slice(0, 200) + "…" : s,
    isNull: false,
    full: s.length > 200 ? s : null,
  };
}

function toCSV(columns, rows) {
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = typeof v === "object" ? JSON.stringify(v) : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [columns.join(",")];
  for (const row of rows) lines.push(columns.map((c) => escape(row[c])).join(","));
  return lines.join("\n");
}

/**
 * Standalone DatabaseAdmin: sidebar + DatabaseAdminPanel content. Use this
 * when there is no parent admin chrome to host the panel.
 */
export default function DatabaseAdmin() {
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get("view") === "query" ? "query" : "browse";

  const setView = (v) => {
    const next = new URLSearchParams(searchParams);
    next.set("view", v);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="w-56 shrink-0 border-r bg-card py-6 px-3 flex flex-col gap-1">
        <div className="px-3 mb-3">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
            Database
          </div>
        </div>
        <button
          onClick={() => setView("browse")}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors text-left ${
            view === "browse"
              ? "bg-primary/10 text-primary font-medium"
              : "text-muted-foreground hover:bg-muted"
          }`}
        >
          <TableIcon className="h-4 w-4" />
          Browse Tables
        </button>
        <button
          onClick={() => setView("query")}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors text-left ${
            view === "query"
              ? "bg-primary/10 text-primary font-medium"
              : "text-muted-foreground hover:bg-muted"
          }`}
        >
          <Play className="h-4 w-4" />
          SQL Query
        </button>
      </aside>
      <DatabaseAdminPanel />
    </div>
  );
}

/**
 * Content-only panel: header + schema sidebar + browse/query right pane.
 * Reads `?view=browse|query` from the URL. Embed inside another layout.
 */
export function DatabaseAdminPanel() {
  const [searchParams] = useSearchParams();
  const view = searchParams.get("view") === "query" ? "query" : "browse";

  const [meta, setMeta] = useState({ dialect: "", quick_queries: [] });
  const [tables, setTables] = useState([]);
  const [tablesLoading, setTablesLoading] = useState(true);
  const [selectedTable, setSelectedTable] = useState(null);
  const [schema, setSchema] = useState(null);
  const [rows, setRows] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage] = useState(25);
  const [sort, setSort] = useState(null);
  const [direction, setDirection] = useState("desc");

  const [sql, setSql] = useState("");
  const [queryResult, setQueryResult] = useState(null);
  const [queryRunning, setQueryRunning] = useState(false);
  const [recent, setRecent] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    } catch {
      return [];
    }
  });

  // --- initial load ---
  useEffect(() => {
    (async () => {
      try {
        const m = await api.get(`${ADMIN_BASE}/meta`);
        setMeta(m.data);
      } catch (e) {
        console.warn("admin meta failed", e);
      }
      try {
        const t = await api.get(`${ADMIN_BASE}/tables`);
        setTables(t.data.tables);
      } catch (e) {
        console.error("admin tables failed", e);
      } finally {
        setTablesLoading(false);
      }
    })();
  }, []);

  const loadSchema = useCallback(async (name) => {
    const r = await api.get(`${ADMIN_BASE}/tables/${encodeURIComponent(name)}/schema`);
    setSchema(r.data);
  }, []);

  const loadRows = useCallback(
    async (name, pageNum, sortField, dir) => {
      if (!name) return;
      const params = new URLSearchParams({
        page: pageNum,
        per_page: perPage,
        direction: dir,
      });
      if (sortField) params.set("sort", sortField);
      const r = await api.get(`${ADMIN_BASE}/tables/${encodeURIComponent(name)}/rows?${params}`);
      setRows(r.data);
    },
    [perPage]
  );

  const selectTable = useCallback(
    async (name) => {
      if (selectedTable === name) {
        setSelectedTable(null);
        setSchema(null);
        setRows(null);
        return;
      }
      setSelectedTable(name);
      setPage(1);
      setSort(null);
      setDirection("desc");
      await Promise.all([loadSchema(name), loadRows(name, 1, null, "desc")]);
    },
    [selectedTable, loadSchema, loadRows]
  );

  const changePage = async (p) => {
    if (!rows || p < 1 || p > rows.total_pages) return;
    setPage(p);
    await loadRows(selectedTable, p, sort, direction);
  };

  const toggleSort = async (col) => {
    const newDir = sort === col && direction === "asc" ? "desc" : "asc";
    const newSort = col;
    setSort(newSort);
    setDirection(newDir);
    setPage(1);
    await loadRows(selectedTable, 1, newSort, newDir);
  };

  const runQuery = async (override) => {
    const sqlToRun = (typeof override === "string" ? override : sql).trim();
    if (!sqlToRun || queryRunning) return;
    if (sqlToRun !== sql) setSql(sqlToRun);
    setQueryRunning(true);
    setQueryResult(null);
    try {
      const r = await api.post(`${ADMIN_BASE}/query`, { sql: sqlToRun });
      setQueryResult(r.data);
      if (!r.data.error) {
        const next = [sqlToRun, ...recent.filter((q) => q !== sqlToRun)].slice(0, 20);
        setRecent(next);
        localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      }
    } catch (e) {
      setQueryResult({
        error: e.response?.data?.detail || e.message,
        columns: [],
        rows: [],
        row_count: 0,
        truncated: false,
        elapsed_ms: 0,
      });
    } finally {
      setQueryRunning(false);
    }
  };

  const removeRecent = (q) => {
    const next = recent.filter((r) => r !== q);
    setRecent(next);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  };

  const clearRecent = () => {
    setRecent([]);
    localStorage.removeItem(RECENT_KEY);
  };

  const copyRows = async () => {
    if (!rows) return;
    await navigator.clipboard.writeText(toCSV(rows.columns, rows.data));
  };

  const exportTable = async () => {
    if (!selectedTable) return;
    const r = await api.get(`${ADMIN_BASE}/export/table/${encodeURIComponent(selectedTable)}.csv`, {
      responseType: "blob",
    });
    triggerDownload(r.data, `${selectedTable}.csv`);
  };

  const copyResult = async () => {
    if (!queryResult) return;
    await navigator.clipboard.writeText(toCSV(queryResult.columns, queryResult.rows));
  };

  const exportQuery = async () => {
    if (!sql.trim()) return;
    const r = await api.post(`${ADMIN_BASE}/export/query.csv`, { sql }, { responseType: "blob" });
    triggerDownload(r.data, "query.csv");
  };

  const quickQueries = useMemo(() => {
    // Dialect-default chips from /api/meta intentionally hidden for now;
    // re-enable by appending `...(meta.quick_queries || [])` below.
    if (!selectedTable) return [];
    const t = selectedTable;
    const tableSpecific = [
      { label: `Preview rows`, sql: `SELECT * FROM ${t} LIMIT 20;` },
      { label: `Row count`, sql: `SELECT COUNT(1) AS total_rows FROM ${t};` },
    ];
    if (meta.dialect === "sqlite") {
      tableSpecific.push({ label: `Column info`, sql: `PRAGMA table_info(${t});` });
    } else if (meta.dialect === "postgresql") {
      tableSpecific.push({
        label: `Column info`,
        sql: `SELECT column_name, data_type, is_nullable FROM information_schema.columns WHERE table_name = '${t}' ORDER BY ordinal_position;`,
      });
    }
    return tableSpecific;
  }, [selectedTable, meta.dialect]);

  const rowsSummary = useMemo(() => {
    if (!rows) return "";
    const start = (rows.page - 1) * rows.per_page + 1;
    const end = Math.min(rows.page * rows.per_page, rows.total);
    return `Showing ${start}-${end} of ${rows.total}`;
  }, [rows]);

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-muted/30">
      <div className="px-8 py-6 border-b bg-background">
        <h1 className="text-xl font-semibold text-foreground">
          {view === "browse" ? "Browse Tables" : "SQL Query"}
          <span className="ml-3 text-xs font-normal text-muted-foreground">
            {meta.dialect || ""}
          </span>
        </h1>
      </div>
      <div className="flex-1 overflow-auto px-8 py-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Schema sidebar (shared) */}
          <Card className="col-span-4 sticky top-0 self-start max-h-[calc(100vh-9rem)] flex flex-col">
            <CardHeader className="pb-5 shrink-0">
              <CardTitle className="text-base flex items-center gap-2">
                <Database className="h-4 w-4 text-muted-foreground" />
                Database Schema
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-y-auto flex-1 min-h-0 scrollbar-skinny">
              <div className="space-y-1.5">
                {tables.map((t) => (
                  <div key={t.name}>
                    <button
                      onClick={() => selectTable(t.name)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                        selectedTable === t.name
                          ? "bg-primary/10 text-primary"
                          : "bg-muted/40 hover:bg-muted text-foreground"
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <TableIcon className="h-3.5 w-3.5" />
                        <span className="font-mono">{t.name}</span>
                      </span>
                      <Badge variant="secondary" className="text-xs">
                        {t.row_count}
                      </Badge>
                    </button>
                    {selectedTable === t.name && schema && (
                      <div className="pl-6 pr-3 py-2 space-y-1">
                        {schema.columns.map((c) => (
                          <div key={c.name} className="flex items-center gap-2 text-xs">
                            {c.primary_key ? (
                              <KeyRound className="h-3 w-3 text-amber-500" />
                            ) : c.foreign_key ? (
                              <LinkIcon className="h-3 w-3 text-purple-500" />
                            ) : (
                              <span className="w-3 text-center text-muted-foreground">·</span>
                            )}
                            <span className="font-mono text-foreground">{c.name}</span>
                            <span className="text-muted-foreground font-mono text-[11px]">
                              ({c.type})
                            </span>
                            {c.primary_key && (
                              <Badge variant="outline" className="text-[10px] px-1 py-0">
                                PK
                              </Badge>
                            )}
                            {!c.nullable && !c.primary_key && (
                              <Badge variant="outline" className="text-[10px] px-1 py-0">
                                NOT NULL
                              </Badge>
                            )}
                            {c.foreign_key && (
                              <Badge
                                variant="outline"
                                className="text-[10px] px-1 py-0"
                                title={c.foreign_key}
                              >
                                FK
                              </Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {tablesLoading && (
                  <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground px-3 py-6">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading tables…
                  </div>
                )}
                {!tablesLoading && tables.length === 0 && (
                  <div className="text-sm text-muted-foreground px-3 py-4">No tables.</div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Right pane */}
          <div className="col-span-8 space-y-6">
            {view === "browse" && (
              <Card>
                <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-base">
                    Table:{" "}
                    <span className="font-mono text-primary">{selectedTable || "—"}</span>
                  </CardTitle>
                  {rows && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">{rowsSummary}</span>
                      <Button variant="outline" size="sm" onClick={copyRows}>
                        <Copy className="h-3 w-3 mr-1" /> Copy Page
                      </Button>
                      <Button variant="outline" size="sm" onClick={exportTable}>
                        <Download className="h-3 w-3 mr-1" /> Export
                      </Button>
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  {!selectedTable && (
                    <div className="text-sm text-muted-foreground py-6">
                      Pick a table on the left.
                    </div>
                  )}
                  {selectedTable && rows && (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={rows.page <= 1}
                          onClick={() => changePage(rows.page - 1)}
                        >
                          <ChevronLeft className="h-3 w-3" /> Previous
                        </Button>
                        <span className="text-xs text-muted-foreground">
                          Page {rows.page} of {rows.total_pages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={rows.page >= rows.total_pages}
                          onClick={() => changePage(rows.page + 1)}
                        >
                          Next <ChevronRight className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="max-h-[60vh] overflow-auto border rounded-xl scrollbar-skinny">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              {rows.columns.map((c) => (
                                <TableHead
                                  key={c}
                                  className="cursor-pointer select-none whitespace-nowrap"
                                  onClick={() => toggleSort(c)}
                                >
                                  {c}
                                  {sort === c && (
                                    <span className="ml-1 text-muted-foreground">
                                      {direction === "asc" ? "▲" : "▼"}
                                    </span>
                                  )}
                                </TableHead>
                              ))}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {rows.data.map((row, i) => (
                              <TableRow key={i}>
                                {rows.columns.map((c) => (
                                  <Cell key={c} value={row[c]} />
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {view === "query" && (
              <>
                <Card>
                  <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Database className="h-4 w-4 text-muted-foreground" />
                      SQL Query
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setSql("")}>
                        <Eraser className="h-3 w-3 mr-1" /> Clear
                      </Button>
                      <Button
                        size="sm"
                        onClick={runQuery}
                        disabled={queryRunning}
                        className="min-w-[96px] justify-center"
                      >
                        <Play className="h-3 w-3 mr-1" />
                        {queryRunning ? "Running…" : "Execute"}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      value={sql}
                      onChange={(e) => setSql(e.target.value)}
                      rows={6}
                      className="font-mono text-sm border-2 border-primary/30 focus:border-primary/60"
                      placeholder="SELECT * FROM ..."
                      onKeyDown={(e) => {
                        if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                          e.preventDefault();
                          runQuery();
                        }
                      }}
                    />
                    {quickQueries.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {quickQueries.map((q) => (
                          <Button
                            key={q.label}
                            variant="outline"
                            size="sm"
                            title={q.sql}
                            onClick={() => runQuery(q.sql)}
                          >
                            {q.label}
                          </Button>
                        ))}
                      </div>
                    )}
                    {recent.length > 0 && (
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-xs text-muted-foreground">
                            Recent queries{recent.length > 5 ? ` (${recent.length})` : ""}:
                          </div>
                          <Button variant="outline" size="sm" onClick={clearRecent}>
                            <Eraser className="h-3 w-3 mr-1" /> Clear
                          </Button>
                        </div>
                        <div className="max-h-[160px] overflow-y-auto scrollbar-skinny border rounded-md">
                          {recent.map((q, i) => (
                            <div
                              key={i}
                              className="group flex items-center gap-1 font-mono text-xs text-foreground hover:bg-muted hover:text-primary cursor-pointer px-2 py-1.5 border-b last:border-b-0"
                              title={q}
                              onClick={() => runQuery(q)}
                            >
                              <span className="truncate flex-1">{q}</span>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeRecent(q);
                                }}
                                className="opacity-0 group-hover:opacity-100 shrink-0 h-6 px-2 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                                title="Remove from history"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {queryResult && (
                  <Card>
                    <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
                      <CardTitle className="text-base">Query Results</CardTitle>
                      {!queryResult.error && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">
                            {queryResult.row_count} rows
                            {queryResult.truncated && (
                              <span className="text-amber-600"> (truncated)</span>
                            )}{" "}
                            · {queryResult.elapsed_ms}ms
                          </span>
                          <Button variant="outline" size="sm" onClick={copyResult}>
                            <Copy className="h-3 w-3 mr-1" /> Copy CSV
                          </Button>
                          <Button variant="outline" size="sm" onClick={exportQuery}>
                            <Download className="h-3 w-3 mr-1" /> Export
                          </Button>
                        </div>
                      )}
                    </CardHeader>
                    <CardContent>
                      {queryResult.error ? (
                        <div className="text-sm text-destructive font-mono bg-destructive/10 border border-destructive/30 rounded-lg p-3">
                          {queryResult.error}
                        </div>
                      ) : (
                        <div className="max-h-[60vh] overflow-auto border rounded-xl scrollbar-skinny">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                {queryResult.columns.map((c) => (
                                  <TableHead key={c} className="whitespace-nowrap">
                                    {c}
                                  </TableHead>
                                ))}
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {queryResult.rows.map((row, i) => (
                                <TableRow key={i}>
                                  {queryResult.columns.map((c) => (
                                    <Cell key={c} value={row[c]} />
                                  ))}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Cell({ value }) {
  const f = formatCell(value);
  return (
    <TableCell
      className={`whitespace-nowrap font-mono text-xs ${
        f.isNull ? "italic text-muted-foreground" : ""
      } ${f.isUuid ? "text-primary cursor-help" : ""}`}
      title={f.full || undefined}
    >
      {f.text}
    </TableCell>
  );
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

